import { useForm} from "react-hook-form";
import { Form } from "../../components/shadcn-ui/form";
import { LEADS_FORM_FIELDS } from "features/loanApplications/formFieldsConfig";
import { DynamicFormField, FieldType } from "../../components/ui/DynamicInput";
import { Card } from "../../components/shadcn-ui/card";
import { Button } from "../../components/shadcn-ui/button";
import { CITY_MAP } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import {
  createLead,
  checkLeadExists,
} from "../../features/leads/services/leadsService";
import { useToast } from "../../customHooks/useToast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../../components/shadcn-ui/dialog";
import { useState,useEffect } from "react";
import { encodeURIComponent, isFieldAsCity } from "@lib/utils";
import * as yup from "yup";

const schema = yup.object().shape({
  phoneNumber: yup
    .string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
});


// Generate dynamic form data type from LEADS_FORM_FIELDS
type FormData = {
  [K in FieldType["name"]]: string;
};

export function CreateLeadPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userDetails } = useAuth();

  const form = useForm<FormData>({
    mode: "all",
    defaultValues: LEADS_FORM_FIELDS.reduce((acc: any, field: FieldType) => {
      acc[field.name] =
        field.name === "referrer" ? userDetails?.email || "" : "";
      return acc;
    }, {} as FormData),
  });

  const [foundLead, setFoundLead] = useState<any>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [showExistingLeadMessage, setShowExistingLeadMessage] = useState(false);

  const onSubmit = async (formState: FormData) => {
    if (disableFields) {
      toast({
        variant: "default",
        description: "A lead with this phone number already exists.",
      });
      return;
    }
    const {
      firstName,
      lastName,
      phoneNumber,
      email,
      addressLineOne,
      addressLineTwo,
      city,
      state,
      postalCode,
      referrer,
      channel,
        pilId,
    } = formState;

    const formattedData = {
      firstName,
      lastName,
      phoneNumber,
      email,
      address: {
        addressLineOne,
        addressLineTwo,
        city,
        state,
        postalCode,
      },
      referral: referrer,
      pilId,
      channel,
    };

    try {
      await createLead(formattedData);
      toast({
        variant: "default",
        title: "Lead created successfully!",
      });
      navigate("/dashboard/leads-list/");
    } catch (err: any) {
      console.error("Error creating lead:", err);
      toast({
        variant: "destructive",
        description: err?.message || "Failed to create lead.",
      });
    }
  };

  const onPhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const phoneNumber = e.target.value;
    if (phoneNumber) {
      try {
        const lead = await checkLeadExists(phoneNumber);
        if (lead) {
          setFoundLead(lead);
          setDisableFields(true);
          setModalOpen(true);
        } else {
          setDisableFields(false);
          setShowExistingLeadMessage(false);
        }
      } catch (error) {
        console.error("Error checking lead:", error);
      }
    }
  };
  const selectedState = form.watch("state");
  useEffect(() => {
    // Reset the city value when state changes
    form.setValue("city", "");
   
  }, [selectedState, form]);



  return (
    <>
      <strong className="block mt-0 mb-2 text-xl bg-[#ebdbcf] p-2 pl-4 rounded-sm">
        Create Lead
      </strong>
      <Card className="p-4 h-full md:pt-6">
        <div className="flex flex-col md:pl-4 flex-wrap">
          <div className="w-full">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-wrap gap-y-4 gap-x-8 w-full justify-center">
                  {LEADS_FORM_FIELDS.map((field: FieldType, index: number) => {
                    const extraProps =
                      field.name === "phoneNumber" ? { onBlur: onPhoneBlur } : {};
                    const isFieldDisabled =
                      disableFields && field.name !== "phoneNumber";
                    const selectedState = form.watch("state"); 
                    
                    const dynamicOptions =
                      isFieldAsCity(field.name)
                      ? CITY_MAP[selectedState] || []
                      : field.options || [];
                    return (
                      <div key={index} className="w-full">
                        <DynamicFormField
                          field={{...field, options : dynamicOptions} as FieldType}
                          isDisabled={
                            isFieldDisabled || field.name === "referrer" || (isFieldAsCity(field.name) && !selectedState)
                          }
                          extraProps={extraProps}
                          {...form.register(field.name)}
                        />
                        {form.formState.errors[field.name] && (
                          <p className="text-red-500 text-xs mt-1">
                            {form.formState.errors[field.name]?.message}
                          </p>
                        )}
                        {field.name === "phoneNumber" &&
                          showExistingLeadMessage && (
                            <p className="text-red-500 text-xs mt-1">
                              A lead with this phone number already exists.{' '}
                              <span
                                className="underline cursor-pointer"
                                onClick={() =>
                                  navigate(
                                    `/dashboard/lead-details/${encodeURIComponent(
                                      foundLead?.phoneNumber
                                    )}`
                                  )
                                }
                              >
                                You can see the details here.
                              </span>
                            </p>
                          )}
                      </div>
                    );
                  })}
                </div>
                <Button type="submit" className="btn-primary mt-4">
                  Submit
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </Card>
  
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader className="p-4">
            <h2 className="text-center text-lg font-semibold">
              Lead Already Exists
            </h2>
          </DialogHeader>
          <div className="p-4">
            <p>
              A lead with phone number <strong>{foundLead?.phoneNumber}</strong>{' '}
              already exists. Would you like to view the lead details?
            </p>
          </div>
          <DialogFooter className="flex justify-center gap-4">
            <Button
              onClick={() => {
                setModalOpen(false);
                navigate(
                  `/dashboard/lead-details/${encodeURIComponent(
                    foundLead?.phoneNumber
                  )}`
                );
              }}
            >
              Yes
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setModalOpen(false);
                setShowExistingLeadMessage(true);
              }}
            >
              No
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
